import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';
import LocationAutocomplete from '../../components/LocationAutocomplete';

// Esquema de validación
const UserSchema = Yup.object().shape({
  document_type: Yup.string().required('El tipo de documento es requerido'),
  document_number: Yup.string()
    .required('El número de documento es requerido')
    .when('document_type', {
      is: 'DNI',
      then: (schema) => schema.matches(/^\d{8}$/, 'El DNI debe tener 8 dígitos')
    }),
  first_name: Yup.string().required('El nombre es requerido'),
  father_last_name: Yup.string().required('El apellido paterno es requerido'),
  mother_last_name: Yup.string().required('El apellido materno es requerido'),
  phone_prefix: Yup.string().required('El prefijo telefónico es requerido'),
  phone_number: Yup.string()
    .required('El número telefónico es requerido')
    .matches(/^\d{9}$/, 'El número telefónico debe tener 9 dígitos'),
  location_ubigeo: Yup.number().required('La ubicación es requerida'),
  address: Yup.string().required('La dirección es requerida'),
  email: Yup.string().email('Email inválido').required('El email es requerido'),
  password: Yup.string().when('isNewUser', {
    is: true,
    then: (schema) => schema
      .required('La contraseña es requerida')
      .min(8, 'La contraseña debe tener al menos 8 caracteres')
  }),
  confirm_password: Yup.string().when('password', {
    is: (val) => val && val.length > 0,
    then: (schema) => schema
      .required('Debe confirmar la contraseña')
      .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden')
  }),
  roles: Yup.array().min(1, 'Debe seleccionar al menos un rol'),
  branches: Yup.array().min(1, 'Debe seleccionar al menos una sucursal'),
  is_enabled: Yup.boolean()
});

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [userBranches, setUserBranches] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Valores iniciales para el formulario
  const initialValues = {
    document_type: 'DNI',
    document_number: '',
    first_name: '',
    father_last_name: '',
    mother_last_name: '',
    phone_prefix: '+51',
    phone_number: '',
    location_ubigeo: '',
    address: '',
    email: '',
    password: '',
    confirm_password: '',
    roles: [],
    branches: [],
    is_enabled: true,
    isNewUser: !isEditMode
  };

  // Cargar datos necesarios
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        
        // Cargar ubicaciones
        const locationsData = await apiService.getLocationUbigeos();
        setLocations(locationsData);
        
        // Cargar roles
        const rolesData = await apiService.getRoles();
        setRoles(rolesData);
        
        // Cargar sucursales
        const branchesData = await apiService.getBranches();
        setBranches(branchesData);
        
        // Si estamos en modo edición, cargar datos del usuario
        if (isEditMode) {
          const userData = await apiService.getUser(id);
          setUser(userData);
          
          // Cargar roles del usuario
          try {
            const userRolesData = await apiService.getUserRoles(id);
            setUserRoles(userRolesData.map(ur => ur.role.id));
          } catch (err) {
            console.error('Error al cargar roles del usuario:', err);
          }
          
          // Cargar sucursales del usuario
          try {
            const userBranchesData = await apiService.getUserBranches(id);
            setUserBranches(userBranchesData.map(ub => ub.branch.id));
          } catch (err) {
            console.error('Error al cargar sucursales del usuario:', err);
          }
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos necesarios. Por favor, intente nuevamente.');
      } finally {
        setLoadingData(false);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const userData = { ...values };
      
      // Eliminar campos que no se envían al API
      delete userData.confirm_password;
      delete userData.isNewUser;
      
      // Si estamos editando y no se cambió la contraseña, eliminarla
      if (isEditMode && !userData.password) {
        delete userData.password;
      }
      
      if (isEditMode) {
        // Actualizar usuario
        await apiService.updateUser(id, userData);
        
        // Actualizar roles y sucursales
        await apiService.updateUserRoles(id, userData.roles);
        await apiService.updateUserBranches(id, userData.branches);
      } else {
        // Crear usuario
        const newUser = await apiService.createUser(userData);
        
        // Asignar roles y sucursales
        await apiService.updateUserRoles(newUser.id, userData.roles);
        await apiService.updateUserBranches(newUser.id, userData.branches);
      }
      
      navigate('/users');
    } catch (err) {
      console.error('Error al guardar usuario:', err);
      setError('Error al guardar los datos del usuario. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  if (loading || loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Preparar valores iniciales si estamos en modo edición
  const formInitialValues = isEditMode && user
    ? {
        ...initialValues,
        ...user,
        roles: userRoles,
        branches: userBranches,
        password: '',
        confirm_password: '',
        isNewUser: false
      }
    : initialValues;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={UserSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting, values, handleChange, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información Personal
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={touched.document_type && Boolean(errors.document_type)}>
                    <InputLabel id="document-type-label">Tipo de Documento</InputLabel>
                    <Field
                      as={Select}
                      name="document_type"
                      labelId="document-type-label"
                      label="Tipo de Documento"
                      value={values.document_type}
                      onChange={(e) => {
                        handleChange(e);
                        // Limpiar el número de documento si cambia el tipo
                        if (e.target.value !== values.document_type) {
                          setFieldValue('document_number', '');
                        }
                      }}
                    >
                      <MenuItem value="DNI">DNI</MenuItem>
                      <MenuItem value="CE">Carnet de Extranjería</MenuItem>
                      <MenuItem value="PAS">Pasaporte</MenuItem>
                    </Field>
                    {touched.document_type && errors.document_type && (
                      <FormHelperText>{errors.document_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Field
                    as={TextField}
                    name="document_number"
                    label="Número de Documento"
                    fullWidth
                    variant="outlined"
                    error={touched.document_number && Boolean(errors.document_number)}
                    helperText={touched.document_number && errors.document_number}
                    inputProps={{
                      maxLength: values.document_type === 'DNI' ? 8 : 15
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="first_name"
                    label="Nombres"
                    fullWidth
                    variant="outlined"
                    error={touched.first_name && Boolean(errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="father_last_name"
                    label="Apellido Paterno"
                    fullWidth
                    variant="outlined"
                    error={touched.father_last_name && Boolean(errors.father_last_name)}
                    helperText={touched.father_last_name && errors.father_last_name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="mother_last_name"
                    label="Apellido Materno"
                    fullWidth
                    variant="outlined"
                    error={touched.mother_last_name && Boolean(errors.mother_last_name)}
                    helperText={touched.mother_last_name && errors.mother_last_name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Contacto
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    as={TextField}
                    name="phone_prefix"
                    label="Prefijo Telefónico"
                    fullWidth
                    variant="outlined"
                    error={touched.phone_prefix && Boolean(errors.phone_prefix)}
                    helperText={touched.phone_prefix && errors.phone_prefix}
                  />
                </Grid>

                <Grid item xs={12} md={9}>
                  <Field
                    as={TextField}
                    name="phone_number"
                    label="Número Telefónico"
                    fullWidth
                    variant="outlined"
                    error={touched.phone_number && Boolean(errors.phone_number)}
                    helperText={touched.phone_number && errors.phone_number}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LocationAutocomplete
                    locations={locations}
                    value={values.location_ubigeo}
                    onChange={(value) => setFieldValue('location_ubigeo', value)}
                    error={touched.location_ubigeo && Boolean(errors.location_ubigeo)}
                    helperText={touched.location_ubigeo && errors.location_ubigeo}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="address"
                    label="Dirección"
                    fullWidth
                    variant="outlined"
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Cuenta
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="email"
                    label="Email"
                    fullWidth
                    variant="outlined"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="password"
                    label={isEditMode ? "Nueva Contraseña (dejar en blanco para mantener)" : "Contraseña"}
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="confirm_password"
                    label="Confirmar Contraseña"
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={touched.confirm_password && Boolean(errors.confirm_password)}
                    helperText={touched.confirm_password && errors.confirm_password}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Roles y Sucursales
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth 
                    error={touched.roles && Boolean(errors.roles)}
                  >
                    <InputLabel id="roles-label">Roles</InputLabel>
                    <Select
                      labelId="roles-label"
                      multiple
                      value={values.roles}
                      onChange={(e) => setFieldValue('roles', e.target.value)}
                      input={<OutlinedInput label="Roles" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const role = roles.find(r => r.id === value);
                            return (
                              <Chip 
                                key={value} 
                                label={role ? role.role_name : value} 
                                size="small" 
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Checkbox checked={values.roles.indexOf(role.id) > -1} />
                          <ListItemText primary={role.role_name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.roles && errors.roles && (
                      <FormHelperText>{errors.roles}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth 
                    error={touched.branches && Boolean(errors.branches)}
                  >
                    <InputLabel id="branches-label">Sucursales</InputLabel>
                    <Select
                      labelId="branches-label"
                      multiple
                      value={values.branches}
                      onChange={(e) => setFieldValue('branches', e.target.value)}
                      input={<OutlinedInput label="Sucursales" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const branch = branches.find(b => b.id === value);
                            return (
                              <Chip 
                                key={value} 
                                label={branch ? branch.branch_name : value} 
                                size="small" 
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          <Checkbox checked={values.branches.indexOf(branch.id) > -1} />
                          <ListItemText primary={branch.branch_name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.branches && errors.branches && (
                      <FormHelperText>{errors.branches}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        name="is_enabled"
                        color="primary"
                        checked={values.is_enabled}
                      />
                    }
                    label="Usuario Activo"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default UserForm; 